import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageCircle, Send, Bot, User, Loader2, Sparkles, Search,
  Key, RotateCcw, Package, Truck, UserCircle, ShoppingBag, 
  CreditCard, Video, ChevronRight, HelpCircle, Mail, Phone,
  ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../utils/api';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelpCenterPage = () => {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [assistant, setAssistant] = useState({ name: 'Zara', emoji: '👩' });
  const [faqs, setFaqs] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await api.get('/auth/me');
          setUser(response.data);
        }
      } catch (error) {
        console.log('Not logged in');
      }
    };
    fetchUser();
  }, []);

  // Fetch FAQs
  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await fetch(`${API_URL}/api/help/faq`);
        const data = await response.json();
        setFaqs(data.faqs || []);
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
      }
    };
    fetchFAQs();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial greeting - fetch assistant first
  useEffect(() => {
    if (messages.length === 0) {
      const initChat = async () => {
        try {
          const response = await fetch(`${API_URL}/api/help/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: "hi",
              session_id: sessionId,
              user_email: user?.email,
              user_id: user?.id
            })
          });
          const data = await response.json();
          
          if (data.session_id) {
            setSessionId(data.session_id);
          }
          if (data.assistant) {
            setAssistant(data.assistant);
          }
          
          const assistantName = data.assistant?.name || 'Zara';
          setMessages([{
            role: 'assistant',
            content: `Hello${user ? ` ${user.first_name || ''}` : ''}! 👋 I'm ${assistantName}, your BidZuu support assistant. I can help you with password resets, returns, orders, shipping, and more. What can I help you with today?`
          }]);
        } catch (error) {
          setMessages([{
            role: 'assistant',
            content: `Hello${user ? ` ${user.first_name || ''}` : ''}! 👋 I'm ${assistant.name}, your BidZuu support assistant. How can I help you today?`
          }]);
        }
      };
      initChat();
    }
  }, []);

  const sendMessage = async (messageText) => {
    const userMessage = messageText || input.trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/help/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          user_email: user?.email,
          user_id: user?.id
        })
      });

      const data = await response.json();
      
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }
      
      // Update assistant if returned from API
      if (data.assistant) {
        setAssistant(data.assistant);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        action: data.action_taken
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or contact support@bidzuu.com.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const helpTopics = [
    { icon: Key, label: 'Password & Login', query: 'I need help with my password or login' },
    { icon: RotateCcw, label: 'Returns & Refunds', query: 'I want to return an item or get a refund' },
    { icon: Package, label: 'Order Status', query: 'I want to check my order status' },
    { icon: Truck, label: 'Shipping', query: 'I have a question about shipping' },
    { icon: UserCircle, label: 'Account Settings', query: 'I need help with my account settings' },
    { icon: ShoppingBag, label: 'Buying & Selling', query: 'How do I buy or sell on BidZuu?' },
    { icon: CreditCard, label: 'Payments', query: 'I have a payment issue' },
    { icon: Video, label: 'Live Streaming', query: 'How do I use BidZuu Live?' },
  ];

  const categoryIcons = {
    'Account': UserCircle,
    'Buying': ShoppingBag,
    'Selling': ShoppingBag,
    'Returns & Refunds': RotateCcw,
    'BidZuu Live': Video
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20 pb-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-8 w-8" />
            </div>
            <h1 className="text-4xl font-bold mb-4">How can we help you?</h1>
            <p className="text-emerald-100 text-lg mb-8">
              Get instant help from our AI assistant or browse our help topics
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    sendMessage(searchQuery);
                    setSearchQuery('');
                  }
                }}
                placeholder="Search for help or ask a question..."
                className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30"
                data-testid="help-search"
              />
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 -mt-8">
          {/* Help Topics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {helpTopics.map((topic, idx) => (
              <button
                key={idx}
                onClick={() => sendMessage(topic.query)}
                className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition flex flex-col items-center text-center group"
                data-testid={`help-topic-${idx}`}
              >
                <div className="w-12 h-12 rounded-full bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-3 transition">
                  <topic.icon className="h-6 w-6 text-emerald-600" />
                </div>
                <span className="text-sm font-medium text-gray-800">{topic.label}</span>
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* AI Chat Section */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-lg">{assistant.emoji}</span>
                </div>
                <div>
                  <h2 className="text-white font-semibold">{assistant.name} - Support Assistant</h2>
                  <p className="text-emerald-100 text-sm">Ask me anything about BidZuu</p>
                </div>
              </div>

              {/* Messages */}
              <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                        msg.role === 'user' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-gradient-to-br from-purple-500 to-indigo-500 text-white'
                      }`}>
                        {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                      </div>
                      <div className={`px-4 py-2.5 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-emerald-500 text-white rounded-br-md'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.action && (
                          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-emerald-600">
                            ✓ Action completed
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-white" />
                      </div>
                      <div className="px-4 py-3 rounded-2xl bg-white shadow-sm border border-gray-100">
                        <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition"
                    disabled={isLoading}
                    data-testid="help-chat-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || isLoading}
                    className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition"
                    data-testid="help-chat-send"
                  >
                    <Send className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-emerald-500" />
                  Frequently Asked Questions
                </h2>
                
                <div className="space-y-3">
                  {faqs.map((category, idx) => {
                    const Icon = categoryIcons[category.category] || HelpCircle;
                    const isExpanded = expandedCategory === idx;
                    
                    return (
                      <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedCategory(isExpanded ? null : idx)}
                          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition"
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="h-5 w-5 text-emerald-500" />
                            <span className="font-medium text-gray-800">{category.category}</span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="p-4 space-y-3 bg-white">
                            {category.questions.map((qa, qIdx) => (
                              <div key={qIdx} className="border-l-2 border-emerald-200 pl-4">
                                <p className="font-medium text-gray-800 text-sm">{qa.q}</p>
                                <p className="text-gray-600 text-sm mt-1">{qa.a}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Need more help?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Our support team is available 24/7 to assist you with any issues.
                </p>
                <div className="space-y-3">
                  <a 
                    href="mailto:support@bidzuu.com" 
                    className="flex items-center gap-3 text-emerald-400 hover:text-emerald-300 transition"
                  >
                    <Mail className="h-5 w-5" />
                    <span>support@bidzuu.com</span>
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  </a>
                  <a 
                    href="tel:+1-800-BIDZUU" 
                    className="flex items-center gap-3 text-emerald-400 hover:text-emerald-300 transition"
                  >
                    <Phone className="h-5 w-5" />
                    <span>1-800-BIDZUU</span>
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpCenterPage;
